import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star, Brain, FileText, TrendingUp, Users, UserPlus, User,
  Send, Copy, RefreshCw, X, CheckCircle, AlertCircle, Calendar,
  ArrowRight, BookOpen, Sparkles, Target
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { InviteRespondentModal } from '../../components/coach/InviteRespondentModal';

interface RespondentData {
  id: string;
  user_id: string | null;
  created_at: string;
  status: string;
  full_name: string;
  email: string;
  has_results: boolean;
  has_ai_analysis: boolean;
  source: 'respondent' | 'pending_invitation';
}

interface CoachFreeData {
  id: string;
  assessment_code: string;
  partner_id: string | null;
  free_profile_complimentary_assessments: number;
  free_profile_completed_assessments: number;
  free_profile_founder_session_expiry: string | null;
  free_profile_founder_session_booked: boolean;
  free_profile_founder_session_booked_at: string | null;
  free_profile_onboarding_choice: string | null;
  free_profile_first_invitation_date: string | null;
  free_profile_first_completion_date: string | null;
  free_profile_first_ai_analysis_date: string | null;
  free_profile_upgrade_status: string;
  created_at: string;
}

export function FreeProfileCoachDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachData, setCoachData] = useState<CoachFreeData | null>(null);
  const [respondents, setRespondents] = useState<RespondentData[]>([]);
  const [aiAnalysesCount, setAiAnalysesCount] = useState(0);
  const [now, setNow] = useState(new Date());
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [bookingUrl, setBookingUrl] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    loadDashboard();
  }, [user?.id]);

  const loadDashboard = async () => {
    if (!user?.id) return;
    try {
      const { data: coach } = await supabase
        .from('coaches')
        .select(`
          id, assessment_code, created_at, partner_id,
          free_profile_complimentary_assessments,
          free_profile_completed_assessments,
          free_profile_founder_session_expiry,
          free_profile_founder_session_booked,
          free_profile_founder_session_booked_at,
          free_profile_onboarding_choice,
          free_profile_first_invitation_date,
          free_profile_first_completion_date,
          free_profile_first_ai_analysis_date,
          free_profile_upgrade_status
        `)
        .eq('user_id', user.id)
        .maybeSingle();

      if (!coach) {
        navigate('/coach');
        return;
      }
      setCoachData(coach as CoachFreeData);

      if (coach?.partner_id) {
        const { data: partner } = await supabase
          .from('partners')
          .select('booking_url')
          .eq('id', coach.partner_id)
          .maybeSingle();
        if (partner?.booking_url) setBookingUrl(partner.booking_url);
      }

      const { data: respData } = await supabase
        .from('respondents')
        .select(`
          id, user_id, created_at, ai_analysis_visible,
          users!respondents_user_id_fkey(full_name, email)
        `)
        .eq('coach_id', coach.id)
        .order('created_at', { ascending: false });

      if (respData) {
        const mapped: RespondentData[] = await Promise.all(
          respData.map(async (r: any) => {
            const { data: results } = await supabase
              .from('results')
              .select('id, ai_analysis')
              .eq('user_id', r.user_id)
              .maybeSingle();

            return {
              id: r.id,
              user_id: r.user_id,
              created_at: r.created_at,
              status: results ? 'completed' : 'invited',
              full_name: r.users?.full_name || 'Unknown',
              email: r.users?.email || '',
              has_results: !!results,
              has_ai_analysis: !!results?.ai_analysis,
              source: 'respondent' as const,
            };
          })
        );

        // Fetch pending invitations (people who were emailed but haven't registered yet)
        const { data: pendingData } = await supabase
          .from('free_profile_pending_invitations')
          .select('id, email, first_name, last_name, invited_at')
          .eq('coach_id', coach.id)
          .order('invited_at', { ascending: false });

        // Merge: exclude pending entries whose email already registered
        const registeredEmails = new Set(mapped.map(r => r.email.toLowerCase()));
        const pendingMapped: RespondentData[] = (pendingData || [])
          .filter((p: any) => !registeredEmails.has(p.email.toLowerCase()))
          .map((p: any) => ({
            id: p.id,
            user_id: null,
            created_at: p.invited_at,
            status: 'email_sent',
            full_name: `${p.first_name}${p.last_name ? ' ' + p.last_name : ''}`,
            email: p.email,
            has_results: false,
            has_ai_analysis: false,
            source: 'pending_invitation' as const,
          }));

        const allRespondents = [...mapped, ...pendingMapped].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        setRespondents(allRespondents);
        setAiAnalysesCount(mapped.filter(r => r.has_ai_analysis).length);
      }
    } catch (err) {
      console.error('Error loading free profile dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const daysRemaining = useMemo(() => {
    if (!coachData?.free_profile_founder_session_expiry) return 0;
    const expiry = new Date(coachData.free_profile_founder_session_expiry);
    const diff = expiry.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [coachData, now]);

  const founderExpired = useMemo(() => {
    if (!coachData?.free_profile_founder_session_expiry) return false;
    return new Date(coachData.free_profile_founder_session_expiry) < now;
  }, [coachData, now]);

  const allAssessmentsUsed = coachData
    ? coachData.free_profile_completed_assessments >= coachData.free_profile_complimentary_assessments
    : false;

  const bannerState = useMemo(() => {
    if (coachData?.free_profile_founder_session_booked) return 'booked';
    if (founderExpired) return 'expired';
    if (daysRemaining <= 1) return 'final_day';
    if (daysRemaining <= 3) return 'expires_soon';
    return 'available';
  }, [coachData, founderExpired, daysRemaining]);

  const activationPanelCopy = useMemo(() => {
    const choice = coachData?.free_profile_onboarding_choice;
    if (choice === 'take_myself') return { title: 'Continue Setting Up Your Own Profile', subtitle: 'Pick up where you left off and complete your own assessment.' };
    if (choice === 'invite_client') return { title: 'Send Your First Client Invitation', subtitle: 'Invite someone you already coach to complete the Profile.' };
    if (choice === 'invite_prospect') return { title: 'Invite Your First Prospect', subtitle: 'Create a valuable first conversation with a potential client.' };
    return { title: 'Create Your First Profile Experience', subtitle: 'Take the Profile yourself or invite a prospect or client.' };
  }, [coachData]);

  if (isLoading) {
    return <LoadingSpinner message="Loading your dashboard..." />;
  }

  const completedCount = coachData?.free_profile_completed_assessments || 0;
  const complimentaryTotal = coachData?.free_profile_complimentary_assessments || 3;
  const remaining = Math.max(0, complimentaryTotal - completedCount);

  const usageCards = [
    { label: 'Complimentary Assessments', value: complimentaryTotal, icon: Star, color: 'bg-sage-100' },
    { label: 'Profiles Completed', value: completedCount, icon: FileText, color: 'bg-teal-50' },
    { label: 'Assessments Remaining', value: remaining, icon: TrendingUp, color: 'bg-orange-40' },
    { label: 'AI Analyses Generated', value: aiAnalysesCount, icon: Brain, color: 'bg-sage-100' },
  ];

  const hasActivity = respondents.length > 0;

  return (
    <div className="min-h-screen freud-bg py-8 px-6">
      <AnimatePresence>
        {showInviteModal && coachData && (
          <InviteRespondentModal
            assessmentCode={coachData.assessment_code}
            coachName={user?.user_metadata?.full_name || user?.email || ''}
            onClose={() => setShowInviteModal(false)}
            isFreeProfile
          />
        )}
      </AnimatePresence>
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Page heading */}
        <div>
          <h1 className="freud-heading-2xl text-chocolate-900">Dashboard</h1>
          <p className="freud-text-base text-chocolate-500">Your coaching overview</p>
        </div>

        {/* 3-assessment exhaustion state */}
        {allAssessmentsUsed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-coral-600 to-coral-700 rounded-2xl p-6 text-white shadow-soft-lg"
          >
            <h3 className="freud-heading-base text-white mb-2">
              Continue Using the Human Potential Profile
            </h3>
            <p className="freud-text-sm text-coral-100 mb-4">
              You have completed your three complimentary Profile experiences. Upgrade to continue inviting respondents, generating AI-assisted analyses and using the Profile as part of your coaching practice.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/pricing')} className="bg-white text-coral-700 px-6 py-2.5 rounded-xl font-semibold hover:bg-coral-50 transition-all">
                View Profile Plans
              </button>
              <button onClick={() => navigate('/discover')} className="border-2 border-white/30 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-white/10 transition-all">
                Explore NLP OS
              </button>
            </div>
          </motion.div>
        )}

        {/* Founder-session urgency banner */}
        <FounderBanner
          state={bannerState}
          daysRemaining={daysRemaining}
          bookedAt={coachData?.free_profile_founder_session_booked_at || null}
          bookingUrl={bookingUrl}
        />

        {/* Primary activation panel */}
        {!allAssessmentsUsed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="freud-card-elevated p-6"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="freud-heading-base text-chocolate-900 mb-1">{activationPanelCopy.title}</h3>
                <p className="freud-text-sm text-chocolate-500">{activationPanelCopy.subtitle}</p>
              </div>
              <div className="flex flex-wrap gap-3 flex-shrink-0">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="freud-btn-primary flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite Someone
                </button>
                <button
                  onClick={() => navigate('/questionnaire')}
                  className="px-6 py-3 rounded-xl border-2 border-chocolate-200 text-chocolate-600 font-medium hover:bg-cream-100 transition-all flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Take the Profile Myself
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Free-account usage cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {usageCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
              className="freud-card-elevated p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 ${stat.color} rounded-xl`}>
                  <stat.icon className="w-5 h-5 text-chocolate-700" />
                </div>
              </div>
              <div className="text-2xl font-bold text-chocolate-900 mb-0.5">{stat.value}</div>
              <div className="text-xs text-chocolate-400">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* How-it-works section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="freud-card-elevated p-6"
        >
          <h3 className="freud-heading-base text-chocolate-900 mb-6">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { num: '1', icon: Send, title: 'Invite', desc: 'Invite yourself, a prospect, client or colleague to complete the Profile.' },
              { num: '2', icon: Brain, title: 'Analyse', desc: 'Review the Profile results and generate the AI-assisted analysis.' },
              { num: '3', icon: MessageIcon, title: 'Converse', desc: 'Use the results to conduct a deeper coaching conversation and identify the person\u2019s next opportunity.' },
            ].map((step) => (
              <div key={step.num} className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-sage-600 text-white flex items-center justify-center text-sm font-bold">
                    {step.num}
                  </div>
                  <step.icon className="w-5 h-5 text-sage-600" />
                  <h4 className="freud-text-base font-semibold text-chocolate-900">{step.title}</h4>
                </div>
                <p className="freud-text-sm text-chocolate-500 pl-11">{step.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Empty state OR invitations/completions */}
        {!hasActivity ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="freud-card-elevated p-8 text-center"
          >
            <div className="inline-flex p-4 bg-sage-100 rounded-2xl mb-4">
              <Sparkles className="w-8 h-8 text-sage-600" />
            </div>
            <h3 className="freud-heading-base text-chocolate-900 mb-2">Start With One Person</h3>
            <p className="freud-text-sm text-chocolate-500 mb-6 max-w-md mx-auto">
              The fastest way to understand the Human Potential Profile is to experience it with a real person.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => setShowInviteModal(true)} className="freud-btn-primary flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Invite Someone
              </button>
              <button onClick={() => navigate('/questionnaire')} className="px-6 py-3 rounded-xl border-2 border-chocolate-200 text-chocolate-600 font-medium hover:bg-cream-100 transition-all flex items-center gap-2">
                <User className="w-4 h-4" />
                Take It Myself
              </button>
            </div>
          </motion.div>
        ) : (
          <InvitationsSection respondents={respondents} assessmentCode={coachData?.assessment_code || ''} onRefresh={loadDashboard} />
        )}

        {/* Resources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="freud-heading-base text-chocolate-900 mb-1">Profile Resources</h3>
              <p className="freud-text-sm text-chocolate-500">
                Invitation templates, conversation structures, and coaching guides.
              </p>
            </div>
            <button
              onClick={() => navigate('/coach/profile-resources')}
              className="flex items-center gap-2 text-sage-600 hover:text-sage-700 font-medium transition-colors"
            >
              View Resources
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Upgrade prompt */}
        {!allAssessmentsUsed && completedCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-gradient-to-r from-sage-600 to-sage-700 rounded-2xl p-6 text-white"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="freud-heading-base text-white mb-1">Ready for More?</h3>
                <p className="freud-text-sm text-sage-100">
                  Upgrade to continue using the Profile with unlimited respondents and full NLP OS access.
                </p>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-white text-sage-700 px-6 py-2.5 rounded-xl font-semibold hover:bg-sage-50 transition-all"
              >
                View Plans
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

function MessageIcon(props: any) {
  return <FileText {...props} />;
}

function FounderBanner({ state, daysRemaining, bookedAt, bookingUrl }: {
  state: string;
  daysRemaining: number;
  bookedAt: string | null;
  bookingUrl: string;
}) {
  const resolvedUrl = bookingUrl || 'https://calendly.com/patrickdahdal/profile-success-session';

  if (state === 'booked') {
    const bookedDate = bookedAt ? new Date(bookedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-sage-600 to-sage-700 rounded-2xl p-6 text-white shadow-soft-lg"
      >
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/20 rounded-xl flex-shrink-0">
            <CheckCircle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="freud-heading-base text-white mb-1">Session Booked</h3>
            <p className="freud-text-sm text-sage-100 mb-3">
              Your Profile-to-Paid-Client Success Session has been booked.
            </p>
            <div className="flex flex-wrap gap-4 freud-text-sm text-sage-50">
              <span>Booked on: {bookedDate}</span>
            </div>
            <div className="mt-4 p-4 bg-white/10 rounded-xl">
              <p className="freud-text-sm text-sage-50 font-medium mb-2">Preparation instructions:</p>
              <p className="freud-text-sm text-sage-100">
                Review the invitation approach, Profile review structure and next-step conversation before your session. Bring questions about your first Profile experience.
              </p>
            </div>
            <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-sage-200 hover:text-white underline freud-text-sm">
              Reschedule
            </a>
          </div>
        </div>
      </motion.div>
    );
  }

  if (state === 'expired') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-chocolate-800 rounded-2xl p-6 text-white"
      >
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/10 rounded-xl flex-shrink-0">
            <Calendar className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="freud-heading-base text-white mb-1">Book a Profile-to-Paid-Client Success Session &mdash; $299</h3>
            <p className="freud-text-sm text-chocolate-200 mb-4">
              Your complimentary Founder session offer has expired. You can still book a session at the standard rate.
            </p>
            <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="inline-block bg-coral-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-coral-700 transition-all">
              Book Session &mdash; $299
            </a>
          </div>
        </div>
      </motion.div>
    );
  }

  const bannerConfig = {
    available: { label: 'Your complimentary Founder session is available', accent: 'from-sage-600 to-sage-700' },
    expires_soon: { label: 'Your complimentary Founder session expires soon', accent: 'from-orange-600 to-orange-700' },
    final_day: { label: 'Final day to claim your complimentary Founder session', accent: 'from-coral-600 to-coral-700' },
  };
  const config = bannerConfig[state as keyof typeof bannerConfig] || bannerConfig.available;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-r ${config.accent} rounded-2xl p-6 text-white shadow-soft-lg`}
    >
      <div className="flex items-start gap-4">
        <div className="p-3 bg-white/20 rounded-xl flex-shrink-0">
          <Calendar className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="freud-heading-base text-white mb-1">Turn Your Profile Assessments Into Paying Clients</h3>
          <p className="freud-text-sm text-white/90 mb-2">
            Book your complimentary one-to-one Profile-to-Paid-Client Success Session with Patrick Dahdal, Founder of NLP OS.
          </p>
          <p className="freud-text-sm text-white/80 mb-3">
            Learn how to attract the right people, conduct a powerful Profile review and transition naturally into a paid coaching conversation.
          </p>
          <p className="freud-text-sm text-white/90 font-medium mb-3">
            Normally $299 &mdash; complimentary for your first 7 days.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-sage-700 px-6 py-2.5 rounded-xl font-semibold hover:bg-sage-50 transition-all">
              Claim My Complimentary Session
            </a>
            <span className="freud-text-sm text-white/90 font-bold">
              {daysRemaining > 0 ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining` : 'Expires today'}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function InvitationsSection({ respondents, assessmentCode, onRefresh }: {
  respondents: RespondentData[];
  assessmentCode: string;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const copyLink = (_email: string) => {
    const link = `https://my.nlpos.app/register?code=${assessmentCode}`;
    navigator.clipboard.writeText(link);
  };

  const cancelInvitation = async (r: RespondentData) => {
    setActionLoading(r.id);
    try {
      if (r.source === 'pending_invitation') {
        await supabase.from('free_profile_pending_invitations').delete().eq('id', r.id);
      } else {
        await supabase.from('respondents').delete().eq('id', r.id);
      }
      onRefresh();
    } catch {
      // best-effort
    } finally {
      setActionLoading(null);
    }
  };

  const newCompletions = respondents.filter(r => r.has_results && !r.has_ai_analysis);

  return (
    <div className="space-y-6">
      {/* Completion / AI analysis state */}
      {newCompletions.map((r) => (
        <motion.div
          key={r.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="freud-card-elevated p-6 border-2 border-sage-300"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-sage-100 rounded-xl flex-shrink-0">
              <Sparkles className="w-6 h-6 text-sage-600" />
            </div>
            <div className="flex-1">
              <h3 className="freud-heading-base text-chocolate-900 mb-1">A New Profile Is Ready</h3>
              <p className="freud-text-sm text-chocolate-500 mb-4">
                {r.full_name} has completed the Human Potential Profile.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => r.user_id && navigate(`/coach/results/${r.user_id}`)}
                  disabled={!r.user_id}
                  className="freud-btn-primary flex items-center gap-2"
                >
                  <Brain className="w-4 h-4" />
                  Generate AI Analysis
                </button>
                <button
                  onClick={() => r.user_id && navigate(`/coach/results/${r.user_id}`)}
                  disabled={!r.user_id}
                  className="px-6 py-3 rounded-xl border-2 border-chocolate-200 text-chocolate-600 font-medium hover:bg-cream-100 transition-all flex items-center gap-2"
                >
                  <BookOpen className="w-4 h-4" />
                  Prepare for Profile Conversation
                </button>
              </div>
              <p className="freud-text-xs text-chocolate-400 mt-3">
                Review the analysis before meeting the respondent. Use the insights as conversation starters rather than treating the report as a fixed judgement.
              </p>
            </div>
          </div>
        </motion.div>
      ))}

      {/* Invitation list */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="freud-card-elevated p-6"
      >
        <h3 className="freud-heading-base text-chocolate-900 mb-4">Current Invitations</h3>
        <div className="space-y-3">
          {respondents.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-4 bg-cream-50 rounded-xl border border-chocolate-100">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-full bg-sage-200 flex items-center justify-center text-sm font-bold text-sage-700 flex-shrink-0">
                  {r.full_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="freud-text-sm font-medium text-chocolate-900 truncate">{r.full_name}</p>
                  <p className="freud-text-xs text-chocolate-400 truncate">{r.email}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      r.status === 'completed'
                        ? 'bg-sage-100 text-sage-700'
                        : r.status === 'email_sent'
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {r.status === 'completed' ? 'Completed' : r.status === 'email_sent' ? 'Email Sent' : 'Invited'}
                    </span>
                    <span className="freud-text-xs text-chocolate-400">
                      {new Date(r.created_at).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {r.has_results && r.has_ai_analysis && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-sage-100 text-sage-700 font-medium">AI Analysis Ready</span>
                )}
                <button
                  onClick={() => copyLink(r.email)}
                  className="p-2 rounded-lg text-chocolate-400 hover:bg-cream-100 hover:text-chocolate-600 transition-all"
                  title="Copy link"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => cancelInvitation(r)}
                  disabled={actionLoading === r.id}
                  className="p-2 rounded-lg text-chocolate-400 hover:bg-warm-100 hover:text-warm-700 transition-all"
                  title="Cancel invitation"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="freud-text-xs text-chocolate-400 mt-4">
          Your complimentary assessment is only counted when the person completes the Profile.
        </p>
      </motion.div>
    </div>
  );
}
